// Shared cover-image pre-warmer.
//
// List surfaces paint their DATA instantly (Redux, module caches, persisted
// snapshots) while the cover <img>s only start downloading when a card
// mounts or scrolls near the viewport — so on slow connections the pictures
// visibly trail the text. The fix pattern: whenever a page of list data
// LANDS, start its covers downloading immediately via detached Image
// objects. By the time a card renders (or scrolls in), the bytes are in
// Chromium's HTTP cache. Chromium coalesces a warm with any in-flight <img>
// request for the same URL, so double-warming costs nothing.
//
// Callers must warm the SAME right-sized variant URL the surface renders
// (utils/coverUrl.js) or the warm fills the cache with bytes no <img> ever
// asks for.

const _warmed = new Set();

// WebKit (WKWebView on iOS, Safari — including iOS Chrome/Edge, which are
// WebKit underneath) has no equivalent of Chromium's delayable-request
// throttling: fetchPriority:'low' does not stop a warm burst from
// saturating the connection, so on phones the warms starved the covers the
// user was actually looking at (artist-page avatar blanked for seconds
// behind a 200-request burst). There, warms get a hard cap, a deferred
// start (visible <img>s claim the pipe first), and a 2-at-a-time trickle.
const _isWebKitOnly = typeof navigator !== 'undefined'
    && /AppleWebKit/i.test(navigator.userAgent)
    && !/Chrom/i.test(navigator.userAgent);

const WEBKIT_CAP = 12;
const WEBKIT_CONCURRENCY = 2;
const _queue = [];
let _inFlight = 0;
let _pumpScheduled = false;

const _pump = () => {
    _pumpScheduled = false;
    while (_inFlight < WEBKIT_CONCURRENCY && _queue.length) {
        const u = _queue.shift();
        _inFlight++;
        const img = new Image();
        img.fetchPriority = 'low';
        img.onload = img.onerror = () => {
            _inFlight--;
            _pump();
        };
        img.src = u;
    }
};

const _schedulePump = () => {
    if (_pumpScheduled) return;
    _pumpScheduled = true;
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(_pump, { timeout: 1500 });
    } else {
        setTimeout(_pump, 300);
    }
};

export const warmImages = (urls, cap = 24) => {
    if (typeof Image === 'undefined') return;
    let started = 0;
    const effectiveCap = _isWebKitOnly ? Math.min(cap, WEBKIT_CAP) : cap;
    for (const u of urls) {
        if (started >= effectiveCap) break;
        if (!u || typeof u !== 'string' || u.startsWith('data:') || _warmed.has(u)) continue;
        _warmed.add(u);
        started++;
        if (_isWebKitOnly) {
            _queue.push(u);
            continue;
        }
        const img = new Image();
        // CRITICAL: warms must never compete with covers the user is looking
        // at. Without this, a page-load burst of warm requests shares
        // bandwidth with the visible <img>s and the covers on screen arrive
        // LAST (measured: 239-request burst pushed even cache hits past 1s).
        img.fetchPriority = 'low';
        img.src = u;
    }
    if (_isWebKitOnly && _queue.length) _schedulePump();
};
